import { Button, Divider, Form, Input, InputNumber, Select, Switch, Tabs, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import ApiService from "../../core/service/api-service";

// PACS'tan gelen DICOM adi "SOYAD^AD" bicimindedir, ama her hastane bu siraya uymuyor.
// Secenekler SONUCU tarif eder: ornek satiri, ayni ham degerin her secenekte RIS'e nasil
// yazildigini gosterir. Kurali RIS degil StudySync uygular; burada sadece deger saklanir.
// Deger id'leri RIS.Data/StaticRepository/PatientNameFormatEnum.cs ile birebir ayni olmali.
const PATIENT_NAME_FORMATS = [
    { value: 1, label: 'Name Surname', raw: 'YILMAZ^AHMET', result: 'AHMET YILMAZ' },
    { value: 2, label: 'Surname Name', raw: 'YILMAZ^AHMET', result: 'YILMAZ AHMET' },
    { value: 3, label: 'Unchanged (RAW)', raw: 'YILMAZ^AHMET', result: 'YILMAZ^AHMET' },
]

export default function PacsSystemCreateEdit({ hospitalId, pacsSystemId, onSuccess }) {
    const { t } = useTranslation();
    const formRef = useRef();
    const [authEnabled, setAuthEnabled] = useState(true)
    const [proxyEnabled, setProxyEnabled] = useState(false)
    const [patientNameFormat, setPatientNameFormat] = useState(1)
    const [pacs, setPacs] = useState({})

    const getPacsSystem = async () => {
        if (pacsSystemId) {
            const response = await ApiService.PacsSystem.Get(hospitalId, pacsSystemId)
            if (response.success) {
                formRef.current.setFieldsValue({
                    ...response.data
                })
                setPacs(response.data)
                setAuthEnabled(response.data.authEnabled)
                setProxyEnabled(response.data.proxyEnabled)
                setPatientNameFormat(response.data.patientNameFormat)
            }
        }
    }

    const save = async (form) => {
        if (pacsSystemId) {
            const response = await ApiService.PacsSystem.Edit(hospitalId, pacsSystemId, form)
            if (response.success)
                onSuccess(hospitalId, response.data)
        } else {
            const response = await ApiService.PacsSystem.Create(hospitalId, form)
            if (response.success)
                onSuccess(hospitalId, response.data)
        }
    }

    useEffect(() => {
        getPacsSystem()
    }, [])

    // Keycloak defaults differ by path: dcm4chee's own Keycloak (8843, openid-connect endpoint,
    // dcm4chee-arc-api client) vs. the DICOM proxy, which exposes its own token endpoint on 8443
    // under /auth/token and expects the `ris` client. Both are only defaults - either can be
    // overwritten by hand, and an edited value is never clobbered by toggling the switch.
    const keycloakDefaults = (address, proxy) => proxy
        ? {
            keycloakTokenUrl: `https://${address}:8443/auth/token`,
            keycloakClientId: `ris`,
        }
        : {
            keycloakTokenUrl: `https://${address}:8843/auth/realms/dcm4che/protocol/openid-connect/token`,
            keycloakClientId: `dcm4chee-arc-api`,
        }

    const autoFill = ({ address, proxy = proxyEnabled }) => {
        formRef.current.setFieldsValue({
            qidoRsAddress: `https://${address}:8443/dcm4chee-arc/aets/${formRef.current.getFieldsValue().aeTitle}/rs`,
            wadoRsAddress: `https://${address}:8443/dcm4chee-arc/aets/${formRef.current.getFieldsValue().aeTitle}/rs`,
            wadoAddress: `https://${address}:8443/dcm4chee-arc/aets/${formRef.current.getFieldsValue().aeTitle}/wado`,
            ...keycloakDefaults(address, proxy),
        })
    }

    // Switching the proxy on/off re-points the Keycloak fields at the other path's defaults, but only
    // where the current value is still the default it would replace (or empty) - a hand-entered URL or
    // client id from an existing PACS survives the toggle.
    const onProxyChange = (checked) => {
        setProxyEnabled(checked)

        const form = formRef.current.getFieldsValue()
        const address = form.serverAddress
        if (!address) return

        const next = keycloakDefaults(address, checked)
        const previous = keycloakDefaults(address, !checked)
        const changes = {}

        Object.keys(next).forEach((field) => {
            if (!form[field] || form[field] === previous[field])
                changes[field] = next[field]
        })

        formRef.current.setFieldsValue(changes)
    }

    const selectedNameFormat = PATIENT_NAME_FORMATS.find((x) => x.value === patientNameFormat)

    return (
        <>
            <Form
                onFinish={save}
                ref={formRef}
                layout={'vertical'}
                initialValues={{
                    authEnabled: authEnabled,
                    proxyEnabled: false,
                    enableStudyFetch: true,
                    patientNameFormat: 1,
                    dimseCPort: 22112
                }}
            >
                <Form.Item
                    label={t('Name')}
                    name={'name'}
                    rules={[
                        { required: true }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label={t('AE Title')}
                    name={'aeTitle'}
                    rules={[
                        { required: true }
                    ]}
                >
                    <Input
                        onChange={(e) => {
                            const address = e.target.value;
                            autoFill({ address: formRef.current.getFieldsValue().serverAddress })
                        }}
                    />
                </Form.Item>

                <Form.Item
                    label={t('Server Address')}
                    name={'serverAddress'}
                    rules={[
                        { required: true }
                    ]}
                >
                    <Input
                        onChange={(e) => {
                            const address = e.target.value;
                            autoFill({ address })
                        }}
                    />
                </Form.Item>

                <Form.Item
                    label={t('Dimse C Port')}
                    name={'dimseCPort'}
                    rules={[
                        { required: true }
                    ]}
                >
                    <InputNumber />
                </Form.Item>

                <Form.Item
                    label={t('QIDO-RS Address')}
                    name={'qidoRsAddress'}
                    rules={[
                        { required: true }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label={t('WADO-RS Address')}
                    name={'wadoRsAddress'}
                    rules={[
                        { required: true }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label={t('WADO Address')}
                    name={'wadoAddress'}
                    rules={[
                        { required: true }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label={t('Enable Study Fetch')}
                    name={'enableStudyFetch'}
                    valuePropName={'checked'}
                    rules={[
                        { required: true }
                    ]}
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    label={t('Patient Name Order')}
                    name={'patientNameFormat'}
                    extra={
                        // Ayarsiz (null) kayitlarda StudySync eski davranisini surdurur; bu yuzden
                        // secim yapilmadigi surece ornek satirini da gostermiyoruz.
                        selectedNameFormat && (
                            <Typography.Text type={'secondary'}>
                                {t('PACS sends')}: <code>{selectedNameFormat.raw}</code>
                                {' → '}
                                {t('Shown in RIS')}: <code>{selectedNameFormat.result}</code>
                            </Typography.Text>
                        )
                    }
                >
                    <Select
                        allowClear
                        placeholder={t('Not set')}
                        onChange={(value) => setPatientNameFormat(value)}
                        options={PATIENT_NAME_FORMATS.map((x) => ({ value: x.value, label: t(x.label) }))}
                    />
                </Form.Item>

                <Form.Item
                    label={t('Auto Delete Studies')}
                    name={'deleteBeforeDays'}
                    rules={[
                        { required: false }
                    ]}
                >
                    <InputNumber />
                </Form.Item>

                <Divider />

                <Form.Item
                    label={t('Auth Enabled')}
                    name={'authEnabled'}
                    valuePropName={'checked'}
                    rules={[
                        { required: true }
                    ]}
                >
                    <Switch onChange={(checked) => setAuthEnabled(checked)} />
                </Form.Item>

                <Form.Item
                    label={t('Proxy Enabled')}
                    name={'proxyEnabled'}
                    valuePropName={'checked'}
                >
                    <Switch onChange={onProxyChange} />
                </Form.Item>

                {
                    /* The proxy mints its tokens with these same credentials, so they stay visible
                       and required while it is on even if Auth Enabled is off. */
                    (authEnabled || proxyEnabled) && (
                        <>
                            <Form.Item
                                label={t('Keycloak Token URL')}
                                name={'keycloakTokenUrl'}
                                rules={[
                                    { required: authEnabled || proxyEnabled }
                                ]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t('Keycloak Client ID')}
                                name={'keycloakClientId'}
                                rules={[
                                    { required: authEnabled || proxyEnabled }
                                ]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t('Keycloak Client Secret')}
                                name={'keycloakClientSecret'}
                                rules={[
                                    { required: authEnabled || proxyEnabled }
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </>
                    )
                }

                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        {t('Save')}
                    </Button>
                </Form.Item>
            </Form>
        </>
    )
}
